'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import * as signalR from '@microsoft/signalr'
import { signalRSessizKayit } from '@/lib/signalrLogger'
import { getBrowserHubUrl } from '@/lib/pharmacyHubUrl'
import { getSignalRAccessToken, isSignalRUnauthorizedError } from '@/lib/signalrAccessToken'
import { refreshTokenSingleton } from '@/lib/tokenRefreshQueue'

export type InternalMessageDto = {
  id: string
  senderUserId: string
  senderUsername: string | null
  body: string
  createdAtUtc: string
}

function normalize(m: {
  id?: string
  senderUserId?: string
  senderUsername?: string | null
  body?: string
  createdAtUtc?: string
}): InternalMessageDto | null {
  if (!m?.id || !m.senderUserId || m.body == null || !m.createdAtUtc) return null
  return {
    id: m.id,
    senderUserId: m.senderUserId,
    senderUsername: m.senderUsername ?? null,
    body: m.body,
    createdAtUtc: m.createdAtUtc,
  }
}

export function usePharmacyInternalMessages(enabled: boolean) {
  const [messages, setMessages] = useState<InternalMessageDto[]>([])
  const [loading, setLoading] = useState(false)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const connRef = useRef<signalR.HubConnection | null>(null)

  useEffect(() => {
    if (!enabled) {
      setConnected(false)
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false

    async function start() {
      setLoading(true)
      setError(null)

      const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenantId') : null

      // Geçmiş: başarısız olsa bile SignalR’a devam et (eczacı/subuser farkında geçmiş tek başına tüm akışı öldürmesin)
      try {
        const res = await fetch(`/api/opas/tenant/internal-messages/history?take=100`, {
          credentials: 'include',
          headers: tenantId ? { 'X-TenantId': tenantId } : {},
        })
        if (res.ok) {
          const raw = (await res.json()) as unknown[]
          const list = raw.map((x) => normalize(x as InternalMessageDto)).filter(Boolean) as InternalMessageDto[]
          if (!cancelled) setMessages(list)
        } else if (!cancelled) {
          const err = await res.json().catch(() => ({}))
          setError((err as { error?: string }).error ?? 'Geçmiş mesajlar yüklenemedi; canlı bağlantı deneniyor.')
        }
      } catch {
        if (!cancelled) setError('Geçmiş mesajlar alınamadı; canlı bağlantı deneniyor.')
      }

      try {
        const hubUrl = getBrowserHubUrl('/hubs/pharmacy-messages')
        if (!hubUrl) throw new Error('Hub adresi oluşturulamadı')

        const initial = await getSignalRAccessToken()
        if (!initial) {
          if (!cancelled) {
            setError('Oturum bulunamadı; mesajlar için giriş yapın.')
            setConnected(false)
          }
          return
        }

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: async () => (await getSignalRAccessToken()) ?? '',
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000])
          .configureLogging(signalRSessizKayit)
          .build()

        connection.on('ReceiveInternalMessage', (payload: unknown) => {
          const n = normalize(payload as InternalMessageDto)
          if (!n) return
          setMessages((prev) => {
            if (prev.some((p) => p.id === n.id)) return prev
            return [...prev, n]
          })
        })

        connection.onreconnecting(() => setConnected(false))
        connection.onreconnected(() => setConnected(true))

        try {
          await connection.start()
        } catch (e) {
          if (isSignalRUnauthorizedError(e)) {
            await refreshTokenSingleton()
            try {
              await connection.start()
            } catch (e2) {
              if (!cancelled) {
                const msg = e2 instanceof Error ? e2.message : 'Bağlantı kurulamadı'
                setError(msg)
                setConnected(false)
              }
              return
            }
          } else {
            throw e
          }
        }
        if (cancelled) {
          await connection.stop()
          return
        }
        connRef.current = connection
        setConnected(true)
        if (!cancelled) setError(null)
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Bağlantı kurulamadı'
          setError((prev) => (prev ? `${prev} ${msg}` : msg))
          setConnected(false)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void start()

    return () => {
      cancelled = true
      const c = connRef.current
      connRef.current = null
      if (c) void c.stop()
      setConnected(false)
    }
  }, [enabled])

  const send = useCallback(async (body: string) => {
    const text = body.trim()
    if (!text) return
    let c = connRef.current
    if (!c) return
    if (c.state !== signalR.HubConnectionState.Connected) {
      try {
        await c.start()
      } catch {
        return
      }
    }
    c = connRef.current
    if (!c || c.state !== signalR.HubConnectionState.Connected) return
    await c.invoke('SendInternalMessage', text)
  }, [])

  return { messages, loading, connected, error, send }
}
